import { lifespan } from '../../../shared/tree'
import type { ShownGroup, ShownPerson } from '@/lib/peopleList'
import { Avatar } from './Avatar'

// The People page's people as one compact list, a row a person (like following lists in other apps): their face,
// their name, and one small line. Grouped only when Group by asks for it. Each row opens their profile, and from
// there their page. How many of your circles someone is in shows only when you're in more than one.
export function PeopleGroups({
  groups,
  circles,
  onOpen,
}: {
  groups: ShownGroup[]
  circles: boolean
  onOpen: (person: ShownPerson) => void
}) {
  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => {
        const people = (
          <ul className="-mx-2 flex flex-col">
            {group.people.map((p) => (
              <li key={p.id}>
                <PersonRow person={p} circles={circles} onOpen={() => onOpen(p)} />
              </li>
            ))}
          </ul>
        )
        // No grouping is one list with nothing to head it.
        if (!group.name) return <div key={group.key}>{people}</div>
        return (
          <section key={group.key} aria-labelledby={`group-${group.key}`} className="flex flex-col gap-1">
            <h2 id={`group-${group.key}`} className="text-sm font-semibold text-balance">
              {group.name}
              <span className="ml-1.5 font-normal text-muted-foreground">{group.people.length}</span>
            </h2>
            {people}
          </section>
        )
      })}
    </div>
  )
}

function PersonRow({ person, circles, onOpen }: { person: ShownPerson; circles: boolean; onOpen: () => void }) {
  const years = lifespan(person)
  const line = [
    person.line,
    !person.living && 'In memory',
    years,
    circles && person.circles > 1 && `${person.circles} circles`,
  ].filter(Boolean)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-14 w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <span className="relative shrink-0">
        <Avatar person={person} className="size-11 text-base" />
        {person.joined && (
          <span
            aria-hidden
            className="absolute right-0 bottom-0 size-3 rounded-full bg-foreground ring-2 ring-background"
          />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[0.9375rem] leading-snug font-semibold">{person.name}</span>
        {line.length > 0 && <span className="truncate text-sub text-muted-foreground">{line.join(' · ')}</span>}
        {person.joined && <span className="sr-only">, in the app</span>}
      </span>
    </button>
  )
}
